import { ApiError, NetworkError } from '../../lib/api';
import type { AuthedRequest } from '../auth/AuthProvider';

export interface WebRTCAnswer {
  sdp: string;
  /** The profile actually played (the main stream when none was requested). */
  profile_token: string;
}

/**
 * Exchanges the browser's SDP offer for the media server's answer. Signalling
 * goes through the VMS API (authorised and audited); only media flows to the
 * media server. Opening a camera can take several seconds.
 */
export const startWebRTC = (
  req: AuthedRequest,
  deviceId: string,
  sdp: string,
  profileToken: string,
  signal?: AbortSignal,
) =>
  req<WebRTCAnswer>(
    'POST',
    `/api/v1/devices/${encodeURIComponent(deviceId)}/live/webrtc`,
    { sdp, profile_token: profileToken },
    signal ? { signal, timeoutMs: 20_000 } : { timeoutMs: 20_000 },
  );

export interface HLSStart {
  /** Playlist on the VMS API; each request needs the access token. */
  url: string;
  profile_token: string;
}

/** Prepares the HLS fallback (used when WebRTC cannot connect). */
export const startHLS = (
  req: AuthedRequest,
  deviceId: string,
  profileToken: string,
  signal?: AbortSignal,
) =>
  req<HLSStart>(
    'POST',
    `/api/v1/devices/${encodeURIComponent(deviceId)}/live/hls`,
    { profile_token: profileToken },
    signal ? { signal, timeoutMs: 20_000 } : { timeoutMs: 20_000 },
  );

/** What the media server receives from a device profile now. */
export interface LiveStats {
  /** False while nobody watches the profile. */
  active: boolean;
  codec: string;
  width: number;
  height: number;
  fps: number;
  audio: string;
  kbps: number;
}

export const liveStatsKey = (deviceId: string, profileToken: string) =>
  ['live-stats', deviceId, profileToken] as const;

export const getLiveStats = (req: AuthedRequest, deviceId: string, profileToken: string) =>
  req<LiveStats>(
    'GET',
    `/api/v1/devices/${encodeURIComponent(deviceId)}/live/stats?profile=${encodeURIComponent(profileToken)}`,
  );

/** "H.265 · 1920×1080 · 25 fps · 2.048 kbps"; unknown parts are left out. */
export function describeLiveStats(s: LiveStats): string {
  const codec = s.codec.replace(/^H(26[45])$/i, 'H.$1');
  const parts = [
    codec,
    s.width && s.height ? `${String(s.width)}×${String(s.height)}` : '',
    s.fps ? `${String(Math.round(s.fps))} fps` : '',
    `${s.kbps.toLocaleString('pt-BR')} kbps`,
  ];
  return parts.filter(Boolean).join(' · ');
}

/** Client-side failures that have no API error code. */
export type LocalFailure = 'ice_failed' | 'timeout' | 'hls_failed' | 'hls_unsupported' | 'stalled';

const localMessages: Record<LocalFailure, string> = {
  ice_failed:
    'A conexão de mídia não pôde ser estabelecida. Verifique se a rede libera a porta WebRTC do servidor de mídia.',
  timeout: 'O vídeo não começou a tempo. Tente novamente.',
  hls_failed:
    'O vídeo não pôde ser reproduzido nem por WebRTC nem por HLS. Verifique a câmera e a rede.',
  hls_unsupported: 'Este navegador não consegue reproduzir vídeo ao vivo (sem WebRTC nem HLS).',
  stalled: 'O vídeo parou de chegar da câmera. Verifique a câmera e a rede.',
};

/**
 * Failures that may clear on their own (camera, link or media server briefly
 * down), so the player tries again by itself. Permissions, missing devices or
 * profiles and an unsupported browser need the operator instead.
 */
export function isTransientLiveFailure(err: unknown): boolean {
  if (typeof err === 'string') return err !== 'hls_unsupported' && err in localMessages;
  if (err instanceof ApiError) return err.status >= 500 || err.code === 'device_not_publishing';
  return err instanceof NetworkError;
}

/** Operator-facing text for a live view failure. */
export function liveErrorMessage(err: unknown): string {
  if (typeof err === 'string' && err in localMessages) return localMessages[err as LocalFailure];
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'no_streams':
        return 'Nenhum stream conhecido para este dispositivo. Execute um teste de conexão em Dispositivos.';
      case 'device_disabled':
        return 'Este dispositivo está desativado.';
      case 'device_not_publishing':
        return 'O dispositivo ainda não está enviando vídeo (conexão reversa RTMP). Confira a URL de envio configurada nele.';
      case 'stream_unavailable':
        return 'O servidor de mídia não conseguiu abrir o vídeo da câmera. Verifique se ela está online.';
      case 'media_unavailable':
        return 'O servidor de mídia está indisponível no momento.';
      case 'forbidden':
        return 'Você não tem permissão para assistir a este dispositivo.';
      case 'not_found':
        return 'Este dispositivo não existe mais.';
      case 'validation_failed':
        return err.fields.profile_token
          ? 'Este perfil de stream não existe mais. Atualize os perfis com um teste de conexão.'
          : 'O navegador enviou uma oferta WebRTC que o servidor não aceitou.';
    }
  }
  if (err instanceof NetworkError) return 'Não foi possível contatar o servidor.';
  return 'Ocorreu um erro inesperado ao abrir o vídeo.';
}
